import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Divider, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment, Badge,
  FormControlLabel, Switch, Autocomplete, CardActionArea
} from '@mui/material';
import {
  Store, Add, Edit, Delete, Search, ShoppingCart,
  Receipt, Inventory, BarChart, QrCode, Warning,
  Remove, AddCircle, RemoveCircle, Print, FileDownload,
  CheckCircle, Payment, Person, LocalOffer, TrendingUp
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const PAY_METHODS = [
  { value: 'cash',              label: 'Cash' },
  { value: 'charge_to_account', label: 'Charge to Student Account' },
  { value: 'mobile_money',      label: 'Mobile Money' },
  { value: 'card',              label: 'Card' },
  { value: 'other',             label: 'Other' },
];
const fmt  = n => `E${parseFloat(n || 0).toFixed(2)}`;
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const ptLabel = v => (v || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

// ── Overview / Stats Tab ───────────────────────────────────────────────────────
function OverviewTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setFrom]   = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setTo]       = useState(new Date().toISOString().split('T')[0]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/shop/stats', { params: { date_from: dateFrom, date_to: dateTo } })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const s = stats?.summary;
  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center" flexWrap="wrap">
        <TextField size="small" type="date" label="From" value={dateFrom} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={dateTo} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="outlined" size="small" onClick={load}>Apply</Button>
        <Button size="small" onClick={() => { const t=new Date().toISOString().split('T')[0]; setFrom(t); setTo(t); }}>Today</Button>
        <Button size="small" onClick={() => {
          const now = new Date(), y = now.getFullYear(), m = now.getMonth();
          setFrom(new Date(y,m,1).toISOString().split('T')[0]);
          setTo(new Date(y,m+1,0).toISOString().split('T')[0]);
        }}>This Month</Button>
      </Stack>

      {loading ? <CircularProgress /> : s && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Transactions',    value: s.transactions || 0,   color: '#0f2b4a' },
              { label: 'Total Revenue',   value: fmt(s.revenue),        color: '#2e7d32' },
              { label: 'Cash Sales',      value: fmt(s.cash),           color: '#1565c0' },
              { label: 'On Account',      value: fmt(s.on_account),     color: '#e65100' },
              { label: 'Mobile Money',    value: fmt(s.mobile),         color: '#6a1b9a' },
            ].map(card => (
              <Grid item xs={6} sm key={card.label}>
                <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${card.color}` }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{card.label}</Typography>
                    <Typography variant="h5" fontWeight={800} sx={{ color: card.color, fontSize: 20 }}>{card.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>🏆 Top Selling Products</Typography>
                  {!stats.top_products?.length && <Typography fontSize={12} color="text.secondary">No sales in this period</Typography>}
                  {stats.top_products?.map((p, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6, borderBottom: '1px solid #f0f0f0' }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>{p.name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{p.department || 'Uncategorised'}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Chip label={`${p.units_sold} sold`} size="small" sx={{ fontSize: 10 }} />
                        <Chip label={fmt(p.revenue)} size="small" color="success" sx={{ fontSize: 10, fontWeight: 700 }} />
                      </Stack>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Stack spacing={2}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography fontWeight={700} gutterBottom>📦 Revenue by Department</Typography>
                    {stats.by_dept?.map((d, i) => (
                      <Box key={i} sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography fontSize={12}>{d.department || 'Uncategorised'}</Typography>
                          <Typography fontSize={12} fontWeight={700}>{fmt(d.revenue)}</Typography>
                        </Box>
                        <LinearProgress variant="determinate"
                          value={stats.summary.revenue > 0 ? (d.revenue / stats.summary.revenue) * 100 : 0}
                          sx={{ height: 5, borderRadius: 3 }} />
                      </Box>
                    ))}
                  </CardContent>
                </Card>

                {stats.low_stock?.length > 0 && (
                  <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #fca5a5' }}>
                    <CardContent>
                      <Typography fontWeight={700} gutterBottom color="error">⚠ Low Stock Alert</Typography>
                      {stats.low_stock.map((p, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, borderBottom: '1px solid #f0f0f0' }}>
                          <Box>
                            <Typography fontSize={12} fontWeight={600}>{p.name}</Typography>
                            <Typography fontSize={10} color="text.secondary">{p.department}</Typography>
                          </Box>
                          <Chip label={`${p.stock_quantity} left`} size="small"
                            color={p.stock_quantity <= 0 ? 'error' : 'warning'} sx={{ fontSize: 10, fontWeight: 700 }} />
                        </Box>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// ── POS Terminal Tab ───────────────────────────────────────────────────────────
function POSTab({ students, academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts]     = useState([]);
  const [departments, setDepts]     = useState([]);
  const [cart, setCart]             = useState([]);
  const [search, setSearch]         = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [payMethod, setPayMethod]   = useState('cash');
  const [buyerType, setBuyerType]   = useState('walkin');
  const [selectedStudent, setStudent] = useState(null);
  const [buyerName, setBuyerName]   = useState('');
  const [selectedYear, setYear]     = useState('');
  const [discount, setDiscount]     = useState('');
  const [tendered, setTendered]     = useState('');
  const [processing, setProcessing] = useState(false);
  const [lastSale, setLastSale]     = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    api.get('/shop/products', { params: { active_only: 'true' } }).then(r => { if (r.data.success) setProducts(r.data.data); });
    api.get('/shop/departments').then(r => { if (r.data.success) setDepts(r.data.data); });
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) { setYear(r.data.data.find(y => y.is_current)?.id || ''); } }).catch(() => {});
  }, []);

  const filtered = products.filter(p => {
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.barcode?.includes(search);
    const matchDept = !filterDept || p.department_id == filterDept;
    return matchSearch && matchDept;
  });

  const addToCart = (product) => {
    setCart(cart => {
      const existing = cart.find(i => i.product_id === product.id);
      if (existing) return cart.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + 1, line_total: (i.quantity + 1) * i.unit_price } : i);
      return [...cart, { product_id: product.id, product_name: product.name, unit_price: parseFloat(product.selling_price), quantity: 1, line_total: parseFloat(product.selling_price), discount: 0, max_stock: product.stock_quantity }];
    });
  };

  const updateQty = (productId, delta) => {
    setCart(cart => cart.map(i => {
      if (i.product_id !== productId) return i;
      const newQty = Math.max(1, Math.min(i.quantity + delta, i.max_stock));
      return { ...i, quantity: newQty, line_total: newQty * i.unit_price - i.discount };
    }));
  };

  const removeFromCart = (productId) => setCart(cart => cart.filter(i => i.product_id !== productId));
  const clearCart = () => { setCart([]); setDiscount(''); setTendered(''); setLastSale(null); };

  const subtotal = cart.reduce((s, i) => s + i.line_total, 0);
  const discAmt  = parseFloat(discount || 0);
  const total    = Math.max(0, subtotal - discAmt);
  const change   = payMethod === 'cash' && tendered ? Math.max(0, parseFloat(tendered) - total) : 0;

  const completeSale = async () => {
    if (!cart.length) return enqueueSnackbar('Cart is empty', { variant: 'warning' });
    if (payMethod === 'charge_to_account' && !selectedStudent) return enqueueSnackbar('Select a student', { variant: 'warning' });
    if (payMethod === 'charge_to_account' && !selectedYear) return enqueueSnackbar('Select academic year', { variant: 'warning' });
    setProcessing(true);
    try {
      const r = await api.post('/shop/sales', {
        buyer_type: buyerType,
        student_id: selectedStudent?.id || null,
        buyer_name: buyerType === 'walkin' ? buyerName : null,
        academic_year_id: selectedYear || null,
        payment_method: payMethod,
        items: cart.map(i => ({ product_id: i.product_id, unit_price: i.unit_price, quantity: i.quantity, discount: i.discount })),
        discount_amount: discAmt,
        amount_tendered: parseFloat(tendered || total),
      });
      setLastSale(r.data);
      enqueueSnackbar(`Sale complete — ${r.data.receipt_number}`, { variant: 'success' });
      setCart([]); setDiscount(''); setTendered(''); setSearch(''); setBuyerName(''); setStudent(null);
      // Refresh products for updated stock
      api.get('/shop/products', { params: { active_only: 'true' } }).then(r => { if (r.data.success) setProducts(r.data.data); });
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error processing sale', { variant: 'error' }); }
    finally { setProcessing(false); }
  };

  return (
    <Grid container spacing={2} sx={{ height: 'calc(100vh - 200px)' }}>
      {/* Product catalogue — left */}
      <Grid item xs={12} md={7}>
        <Stack spacing={1.5} sx={{ height: '100%' }}>
          {/* Search & filter */}
          <Stack direction="row" spacing={1}>
            <TextField size="small" placeholder="Search products or scan barcode…" value={search}
              onChange={e => setSearch(e.target.value)} inputRef={searchRef} sx={{ flexGrow: 1 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><QrCode fontSize="small" /></InputAdornment> }} />
            <TextField select size="small" label="Department" value={filterDept} onChange={e => setFilterDept(e.target.value)} sx={{ width: 160 }}>
              <MenuItem value="">All</MenuItem>
              {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
            </TextField>
          </Stack>

          {/* Product grid */}
          <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
            <Grid container spacing={1}>
              {filtered.length === 0 && (
                <Grid item xs={12}><Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                  <Typography color="text.secondary">No products found</Typography>
                </Paper></Grid>
              )}
              {filtered.map(p => (
                <Grid item xs={6} sm={4} md={4} lg={3} key={p.id}>
                  <Card variant="outlined" sx={{ borderRadius: 2, opacity: p.stock_quantity <= 0 ? 0.5 : 1,
                    border: p.stock_quantity <= p.reorder_level && p.stock_quantity > 0 ? '1px solid #fbbf24' : undefined }}>
                    <CardActionArea onClick={() => p.stock_quantity > 0 && addToCart(p)} disabled={p.stock_quantity <= 0}>
                      <CardContent sx={{ pb: '8px !important', pt: 1.5 }}>
                        <Typography fontSize={12} fontWeight={700} sx={{ lineHeight: 1.3, mb: 0.5 }} noWrap>{p.name}</Typography>
                        <Typography fontSize={10} color="text.secondary" noWrap>{p.department_name}</Typography>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.8 }}>
                          <Typography fontSize={14} fontWeight={800} color="success.main">{fmt(p.selling_price)}</Typography>
                          <Chip label={p.stock_quantity <= 0 ? 'Out' : `${p.stock_quantity}`} size="small"
                            color={p.stock_quantity <= 0 ? 'error' : p.stock_quantity <= p.reorder_level ? 'warning' : 'default'}
                            sx={{ fontSize: 9, height: 18 }} />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      </Grid>

      {/* Cart & payment — right */}
      <Grid item xs={12} md={5}>
        <Card variant="outlined" sx={{ borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <CardContent sx={{ flexGrow: 0, pb: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ShoppingCart fontSize="small" /> Cart
                {cart.length > 0 && <Chip label={cart.reduce((s,i)=>s+i.quantity,0)} size="small" color="primary" sx={{ ml: 0.5 }} />}
              </Typography>
              {cart.length > 0 && <Button size="small" color="error" onClick={clearCart}>Clear</Button>}
            </Stack>
          </CardContent>

          {/* Cart items */}
          <Box sx={{ overflowY: 'auto', flexGrow: 1, px: 2 }}>
            {cart.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, bgcolor: '#fafafa' }}>
                <ShoppingCart sx={{ fontSize: 40, color: 'text.disabled', mb: 0.5 }} />
                <Typography fontSize={12} color="text.secondary">Cart is empty</Typography>
                <Typography fontSize={11} color="text.secondary">Click a product to add it</Typography>
              </Paper>
            ) : cart.map(item => (
              <Box key={item.product_id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.8, borderBottom: '1px solid #f0f0f0' }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography fontSize={12} fontWeight={600} noWrap>{item.product_name}</Typography>
                  <Typography fontSize={11} color="text.secondary">{fmt(item.unit_price)} each</Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.3}>
                  <IconButton size="small" onClick={() => item.quantity <= 1 ? removeFromCart(item.product_id) : updateQty(item.product_id, -1)}>
                    <RemoveCircle sx={{ fontSize: 18, color: item.quantity <= 1 ? 'error.main' : 'text.secondary' }} />
                  </IconButton>
                  <Typography fontWeight={700} sx={{ minWidth: 20, textAlign: 'center' }}>{item.quantity}</Typography>
                  <IconButton size="small" onClick={() => updateQty(item.product_id, 1)} disabled={item.quantity >= item.max_stock}>
                    <AddCircle sx={{ fontSize: 18, color: 'success.main' }} />
                  </IconButton>
                </Stack>
                <Typography fontWeight={700} fontSize={13} sx={{ minWidth: 60, textAlign: 'right', color: 'success.main' }}>
                  {fmt(item.line_total)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Payment section */}
          {cart.length > 0 && (
            <CardContent sx={{ pt: 1, borderTop: '1px solid #eee' }}>
              {/* Totals */}
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontSize={12} color="text.secondary">Subtotal</Typography>
                  <Typography fontSize={12}>{fmt(subtotal)}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography fontSize={12} color="text.secondary">Discount</Typography>
                  <TextField size="small" type="number" value={discount} onChange={e => setDiscount(e.target.value)}
                    sx={{ width: 80 }} inputProps={{ min: 0, step: '0.50' }}
                    InputProps={{ startAdornment: <InputAdornment position="start" sx={{ fontSize: 11 }}>E</InputAdornment> }} />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={800} fontSize={16}>TOTAL</Typography>
                  <Typography fontWeight={800} fontSize={16} color="primary">{fmt(total)}</Typography>
                </Stack>
              </Stack>

              {/* Customer */}
              <Stack spacing={1} sx={{ mb: 1.5 }}>
                <TextField select size="small" fullWidth label="Buyer Type" value={buyerType} onChange={e => { setBuyerType(e.target.value); setPayMethod(e.target.value === 'student' ? 'cash' : 'cash'); }}>
                  <MenuItem value="walkin">Walk-in Customer</MenuItem>
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                </TextField>
                {buyerType === 'student' ? (
                  <Autocomplete size="small" options={students}
                    getOptionLabel={s => `${s.first_name} ${s.last_name} (${s.admission_number})`}
                    value={selectedStudent}
                    onChange={(_, v) => setStudent(v)}
                    renderInput={p => <TextField {...p} label="Select Student" />} />
                ) : (
                  <TextField size="small" fullWidth label="Name (optional)" value={buyerName} onChange={e => setBuyerName(e.target.value)} />
                )}
                <TextField select size="small" fullWidth label="Payment Method" value={payMethod} onChange={e => setPayMethod(e.target.value)}>
                  {PAY_METHODS.filter(m => buyerType === 'student' || m.value !== 'charge_to_account').map(m => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </TextField>
                {payMethod === 'charge_to_account' && (
                  <TextField select size="small" fullWidth label="Academic Year *" value={selectedYear} onChange={e => setYear(e.target.value)}>
                    {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
                  </TextField>
                )}
                {payMethod === 'cash' && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" type="number" label="Amount Tendered" value={tendered} onChange={e => setTendered(e.target.value)}
                      sx={{ flexGrow: 1 }} InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} />
                    {tendered && parseFloat(tendered) >= total && (
                      <Chip label={`Change: ${fmt(change)}`} color="success" size="small" sx={{ fontWeight: 700 }} />
                    )}
                  </Stack>
                )}
              </Stack>

              <Button fullWidth variant="contained" size="large" onClick={completeSale} disabled={processing}
                startIcon={processing ? <CircularProgress size={18} color="inherit" /> : <CheckCircle />}
                sx={{ bgcolor: '#0f2b4a', py: 1.5, fontSize: 14, fontWeight: 700 }}>
                {processing ? 'Processing…' : `Complete Sale — ${fmt(total)}`}
              </Button>

              {lastSale && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Alert severity="success" sx={{ flexGrow: 1, fontSize: 11 }}>
                    {lastSale.receipt_number} ✓
                    {lastSale.fee_invoice_id && ' · Charged to account'}
                  </Alert>
                  <Button size="small" variant="outlined" startIcon={<Print />}
                    onClick={() => { const token = localStorage.getItem('sms_token'); window.open(`${API_BASE}/shop/receipt/${lastSale.sale_id}?token=${token}`, '_blank'); }}>
                    Print
                  </Button>
                </Stack>
              )}
            </CardContent>
          )}
        </Card>
      </Grid>
    </Grid>
  );
}

// ── Product Catalogue / Stock Tab ──────────────────────────────────────────────
function CatalogueTab() {
  const { enqueueSnackbar } = useSnackbar();
  const [products, setProducts]     = useState([]);
  const [departments, setDepts]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [lowOnly, setLowOnly]       = useState(false);
  const [dialog, setDialog]         = useState(false);
  const [editing, setEditing]       = useState(null);
  const [stockDialog, setStockDialog] = useState(null);
  const [deptDialog, setDeptDialog] = useState(false);

  const BLANK = { barcode:'', name:'', description:'', department_id:'', unit:'each', selling_price:'', cost_price:'', stock_quantity:0, reorder_level:5 };
  const [form, setForm] = useState(BLANK);
  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const [stockForm, setStockForm] = useState({ movement_type:'purchase', quantity:'', unit_cost:'', reference:'', notes:'' });
  const [deptForm, setDeptForm] = useState({ name:'', description:'' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, dr] = await Promise.all([
        api.get('/shop/products', { params: { search: search||undefined, department_id: filterDept||undefined, low_stock: lowOnly?'true':undefined, active_only:'false' } }),
        api.get('/shop/departments')
      ]);
      if (pr.data.success) setProducts(pr.data.data);
      if (dr.data.success) setDepts(dr.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [search, filterDept, lowOnly, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name || !form.selling_price) return enqueueSnackbar('Name and price required', { variant: 'warning' });
    try {
      if (editing) { await api.put(`/shop/products/${editing.id}`, { ...form, is_active: editing.is_active ?? 1 }); enqueueSnackbar('Updated', { variant: 'success' }); }
      else { await api.post('/shop/products', form); enqueueSnackbar('Product added', { variant: 'success' }); }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const adjustStock = async () => {
    try {
      const qty = parseInt(stockForm.quantity);
      if (!qty) return enqueueSnackbar('Enter a quantity', { variant: 'warning' });
      const finalQty = stockForm.movement_type === 'purchase' || stockForm.movement_type === 'opening' ? Math.abs(qty) : -Math.abs(qty);
      await api.post('/shop/stock/adjust', { product_id: stockDialog.id, quantity: finalQty, ...stockForm });
      enqueueSnackbar('Stock updated', { variant: 'success' });
      setStockDialog(null); load();
    } catch (err) { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.delete(`/shop/products/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Cannot delete', { variant: 'error' }); }
  };

  const saveDept = async () => {
    try {
      await api.post('/shop/departments', deptForm);
      enqueueSnackbar('Department created', { variant: 'success' });
      setDeptDialog(false); setDeptForm({ name:'', description:'' }); load();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <TextField size="small" placeholder="Search name or barcode…" value={search} onChange={e => setSearch(e.target.value)} sx={{ minWidth: 220 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Department" value={filterDept} onChange={e => setFilterDept(e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="">All</MenuItem>
          {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>
        <FormControlLabel control={<Switch size="small" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} />}
          label={<Typography fontSize={12}>Low stock only</Typography>} />
        <Box sx={{ flexGrow: 1 }} />
        <Chip label={`${products.filter(p => p.stock_quantity <= p.reorder_level).length} low stock`} size="small" color="warning" />
        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('products', ['Name','Department','Barcode','Price','Cost','Stock','Reorder','Unit'],
            products.map(p => [p.name, p.department_name||'', p.barcode||'', p.selling_price, p.cost_price||'', p.stock_quantity, p.reorder_level, p.unit]))
        }>Export</Button>
        <Button size="small" variant="outlined" onClick={() => setDeptDialog(true)}>+ Dept</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setForm({...BLANK}); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>Add Product</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 560 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Barcode','Name','Department','Price','Cost','Stock','Reorder','Status','Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {products.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No products found</TableCell></TableRow>}
                {products.map((p, i) => {
                  const low = p.stock_quantity <= p.reorder_level;
                  return (
                    <TableRow key={p.id} hover sx={{ bgcolor: p.stock_quantity <= 0 ? '#fff3f3' : low ? '#fffde7' : i % 2 === 0 ? 'white' : '#fafafa', opacity: p.is_active ? 1 : 0.5 }}>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{p.barcode || '—'}</TableCell>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={600}>{p.name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{p.unit}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{p.department_name || '—'}</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'success.main', fontSize: 13 }}>{fmt(p.selling_price)}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{p.cost_price > 0 ? fmt(p.cost_price) : '—'}</TableCell>
                      <TableCell>
                        <Chip label={p.stock_quantity} size="small"
                          color={p.stock_quantity <= 0 ? 'error' : low ? 'warning' : 'success'}
                          sx={{ fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{p.reorder_level}</TableCell>
                      <TableCell>
                        <Chip label={p.is_active ? 'Active' : 'Inactive'} size="small" color={p.is_active ? 'success' : 'default'} sx={{ fontSize: 10 }} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.3}>
                          <Tooltip title="Adjust stock">
                            <IconButton size="small" color="warning" onClick={() => { setStockDialog(p); setStockForm({ movement_type:'purchase', quantity:'', unit_cost: p.cost_price||'', reference:'', notes:'' }); }}>
                              <Inventory fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditing(p); setForm({ barcode: p.barcode||'', name: p.name, description: p.description||'', department_id: p.department_id||'', unit: p.unit, selling_price: p.selling_price, cost_price: p.cost_price||'', stock_quantity: p.stock_quantity, reorder_level: p.reorder_level }); setDialog(true); }}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => del(p.id)}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Product Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>{editing ? 'Edit Product' : 'Add Product'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={6}><TextField fullWidth size="small" label="Barcode (optional)" value={form.barcode} onChange={setF('barcode')} InputProps={{ startAdornment: <InputAdornment position="start"><QrCode fontSize="small" /></InputAdornment> }} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth size="small" label="Unit" value={form.unit} onChange={setF('unit')}>
                {['each','pair','set','box','pack','kg','litre','dozen','ream'].map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Product Name *" value={form.name} onChange={setF('name')} /></Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Department" value={form.department_id} onChange={setF('department_id')}>
                <MenuItem value="">No department</MenuItem>
                {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Selling Price (E) *" value={form.selling_price} onChange={setF('selling_price')} InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} /></Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Cost Price (E)" value={form.cost_price} onChange={setF('cost_price')} InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} /></Grid>
            {!editing && <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Opening Stock" value={form.stock_quantity} onChange={setF('stock_quantity')} /></Grid>}
            <Grid item xs={editing ? 12 : 6}><TextField fullWidth size="small" type="number" label="Reorder Level" value={form.reorder_level} onChange={setF('reorder_level')} helperText="Alert when stock falls to this" /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={form.description} onChange={setF('description')} multiline rows={2} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} sx={{ bgcolor: '#0f2b4a' }}>{editing ? 'Update' : 'Add Product'}</Button>
        </DialogActions>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={!!stockDialog} onClose={() => setStockDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Adjust Stock — {stockDialog?.name}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ fontSize: 12 }}>
                Current stock: <strong>{stockDialog?.stock_quantity}</strong> {stockDialog?.unit}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth size="small" label="Movement Type" value={stockForm.movement_type} onChange={e => setStockForm(f => ({ ...f, movement_type: e.target.value }))}>
                <MenuItem value="purchase">Stock Received (Purchase)</MenuItem>
                <MenuItem value="adjustment">Manual Adjustment</MenuItem>
                <MenuItem value="return">Return to Supplier</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="number" label="Quantity" value={stockForm.quantity}
                onChange={e => setStockForm(f => ({ ...f, quantity: e.target.value }))}
                helperText={stockForm.movement_type === 'purchase' ? '+adds to stock' : 'amount to adjust'} />
            </Grid>
            <Grid item xs={6}><TextField fullWidth size="small" type="number" label="Unit Cost (E)" value={stockForm.unit_cost} onChange={e => setStockForm(f => ({ ...f, unit_cost: e.target.value }))} InputProps={{ startAdornment: <InputAdornment position="start">E</InputAdornment> }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Reference (invoice/PO)" value={stockForm.reference} onChange={e => setStockForm(f => ({ ...f, reference: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Notes" value={stockForm.notes} onChange={e => setStockForm(f => ({ ...f, notes: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStockDialog(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={adjustStock}>Update Stock</Button>
        </DialogActions>
      </Dialog>

      {/* Department Dialog */}
      <Dialog open={deptDialog} onClose={() => setDeptDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Department</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12}><TextField fullWidth size="small" label="Name *" value={deptForm.name} onChange={e => setDeptForm(f => ({ ...f, name: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={deptForm.description} onChange={e => setDeptForm(f => ({ ...f, description: e.target.value }))} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeptDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveDept} sx={{ bgcolor: '#0f2b4a' }}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Sales History Tab ──────────────────────────────────────────────────────────
function SalesHistoryTab({ students }) {
  const { enqueueSnackbar } = useSnackbar();
  const [sales, setSales]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [dateFrom, setFrom]       = useState(new Date().toISOString().split('T')[0]);
  const [dateTo, setTo]           = useState(new Date().toISOString().split('T')[0]);
  const [filterBuyer, setBuyer]   = useState('');
  const [selectedSale, setSaleDetail] = useState(null);
  const [saleDetail, setSaleData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/shop/sales', { params: { date_from: dateFrom, date_to: dateTo, buyer_type: filterBuyer || undefined } });
      if (r.data.success) setSales(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, filterBuyer, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    setSaleDetail(id); setSaleData(null);
    const r = await api.get(`/shop/sales/${id}`);
    if (r.data.success) setSaleData(r.data.data);
  };

  const totalRev = sales.reduce((s, sale) => s + parseFloat(sale.total_amount || 0), 0);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" alignItems="center">
        <TextField size="small" type="date" label="From" value={dateFrom} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={dateTo} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField select size="small" label="Buyer Type" value={filterBuyer} onChange={e => setBuyer(e.target.value)} sx={{ width: 150 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="student">Student</MenuItem>
          <MenuItem value="walkin">Walk-in</MenuItem>
          <MenuItem value="staff">Staff</MenuItem>
        </TextField>
        <Chip label={`${sales.length} sales`} size="small" />
        <Chip label={fmt(totalRev)} size="small" color="success" sx={{ fontWeight: 700 }} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('sales', ['Receipt','Date','Buyer','Method','Total','Items'],
            sales.map(s => [s.receipt_number, fmtD(s.sale_date), s.first_name ? `${s.first_name} ${s.last_name}` : s.buyer_name || 'Walk-in', ptLabel(s.payment_method), s.total_amount, s.item_count]))
        }>Export</Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Receipt','Date','Buyer','Method','Items','Total','Cashier',''].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No sales found</TableCell></TableRow>}
                {sales.map((s, i) => (
                  <TableRow key={s.id} hover sx={{ bgcolor: i % 2 === 0 ? 'white' : '#fafafa', cursor: 'pointer' }} onClick={() => openDetail(s.id)}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, color: 'primary.main' }}>{s.receipt_number}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{fmtD(s.sale_date)}</TableCell>
                    <TableCell>
                      {s.first_name ? <><Typography fontSize={12} fontWeight={600}>{s.last_name}, {s.first_name}</Typography><Typography fontSize={10} color="text.secondary">{s.admission_number}</Typography></>
                        : <Typography fontSize={12}>{s.buyer_name || 'Walk-in'}</Typography>}
                    </TableCell>
                    <TableCell><Chip label={ptLabel(s.payment_method)} size="small" color={s.payment_method === 'charge_to_account' ? 'warning' : s.payment_method === 'cash' ? 'default' : 'primary'} sx={{ fontSize: 10 }} /></TableCell>
                    <TableCell align="center" sx={{ fontSize: 12 }}>{s.item_count}</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 13, color: 'success.main' }}>{fmt(s.total_amount)}</TableCell>
                    <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{s.served_by_name || '—'}</TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <Tooltip title="Print receipt">
                        <IconButton size="small" onClick={() => { const token = localStorage.getItem('sms_token'); window.open(`${API_BASE}/shop/receipt/${s.id}?token=${token}`, '_blank'); }}>
                          <Print fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Sale Detail Dialog */}
      <Dialog open={!!selectedSale} onClose={() => { setSaleDetail(null); setSaleData(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Sale Detail — {saleDetail?.receipt_number}</DialogTitle>
        <DialogContent dividers>
          {!saleDetail ? <CircularProgress /> : (
            <Box>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Date</Typography><Typography fontSize={13}>{fmtD(saleDetail.sale_date)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Cashier</Typography><Typography fontSize={13}>{saleDetail.cashier || '—'}</Typography></Grid>
                {saleDetail.first_name && <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Student</Typography><Typography fontSize={13}>{saleDetail.first_name} {saleDetail.last_name} ({saleDetail.admission_number})</Typography></Grid>}
                <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Payment</Typography><Chip label={ptLabel(saleDetail.payment_method)} size="small" /></Grid>
              </Grid>
              <Table size="small">
                <TableHead><TableRow sx={{ bgcolor: '#f5f6fa' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Product</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Unit</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">Total</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {saleDetail.items?.map(item => (
                    <TableRow key={item.id}>
                      <TableCell sx={{ fontSize: 12 }}>{item.product_name}</TableCell>
                      <TableCell sx={{ fontSize: 12 }} align="right">{item.quantity}</TableCell>
                      <TableCell sx={{ fontSize: 12 }} align="right">{fmt(item.unit_price)}</TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }} align="right">{fmt(item.line_total)}</TableCell>
                    </TableRow>
                  ))}
                  {saleDetail.discount_amount > 0 && <TableRow><TableCell colSpan={3} sx={{ fontSize: 12 }}>Discount</TableCell><TableCell sx={{ fontSize: 12, color: 'error.main' }} align="right">-{fmt(saleDetail.discount_amount)}</TableCell></TableRow>}
                  <TableRow sx={{ bgcolor: '#f5f6fa' }}><TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTAL</TableCell><TableCell sx={{ fontWeight: 700, fontSize: 14 }} align="right">{fmt(saleDetail.total_amount)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSaleDetail(null); setSaleData(null); }}>Close</Button>
          {saleDetail && <Button variant="outlined" startIcon={<Print />} onClick={() => { const token = localStorage.getItem('sms_token'); window.open(`${API_BASE}/shop/receipt/${selectedSale}?token=${token}`, '_blank'); }}>Print Receipt</Button>}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function SchoolShop() {
  const [tab, setTab]           = useState(1); // Start on POS
  const [students, setStudents] = useState([]);
  const [academicYears, setAY]  = useState([]);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    import('../services/api').then(({ studentsAPI }) => {
      studentsAPI.getAll({ limit: 2000, status: 'active' }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
    });
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Store color="primary" /> School Shop & Bookshop
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Point of sale · Product catalogue · Stock management · Charge to student account
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />}    label="Overview"   iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<ShoppingCart fontSize="small" />} label="POS Terminal" iconPosition="start" sx={{ fontSize: 12, fontWeight: tab === 1 ? 700 : 400 }} />
        <Tab icon={<Inventory fontSize="small" />}  label="Products & Stock" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Receipt fontSize="small" />}    label="Sales History" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab /></TabPanel>
      <TabPanel value={tab} index={1}><POSTab students={students} academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={2}><CatalogueTab /></TabPanel>
      <TabPanel value={tab} index={3}><SalesHistoryTab students={students} /></TabPanel>
    </Box>
  );
}
